import React from 'react';

/**
 * Custom Pagination Component
 * Replaces react-paginate with a pure React + SCSS solution
 *
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {function} onPageChange - Callback function when page changes, receives page number (1-indexed)
 * @param {number} pageRangeDisplayed - Number of pages to show around current page (default: 3)
 * @param {number} marginPagesDisplayed - Number of pages to show at start and end (default: 2)
 */
const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    pageRangeDisplayed = 3,
    marginPagesDisplayed = 2,
}) => {
    /**
     * Generate array of page numbers to display with breaks
     * Logic matches ReactPaginate behavior
     */
    const getPageNumbers = () => {
        if (totalPages <= 0) return [];

        const pages = [];
        const halfRange = Math.floor(pageRangeDisplayed / 2);

        // Calculate the range of pages to show around current page
        let startPage = Math.max(1, currentPage - halfRange);
        let endPage = Math.min(totalPages, currentPage + halfRange);

        // Adjust if we're near the start or end
        if (currentPage <= halfRange) {
            endPage = Math.min(totalPages, pageRangeDisplayed);
        }
        if (currentPage >= totalPages - halfRange) {
            startPage = Math.max(1, totalPages - pageRangeDisplayed + 1);
        }

        // Add first pages
        for (let i = 1; i <= Math.min(marginPagesDisplayed, totalPages); i++) {
            pages.push(i);
        }

        // Add break before middle section if needed
        if (startPage > marginPagesDisplayed + 1) {
            pages.push('break-start');
        }

        // Add middle section (around current page)
        const middleStart = Math.max(marginPagesDisplayed + 1, startPage);
        const middleEnd = Math.min(totalPages - marginPagesDisplayed, endPage);

        for (let i = middleStart; i <= middleEnd; i++) {
            if (!pages.includes(i)) {
                pages.push(i);
            }
        }

        // Add break before last pages if needed
        if (endPage < totalPages - marginPagesDisplayed) {
            pages.push('break-end');
        }

        // Add last pages
        for (let i = Math.max(totalPages - marginPagesDisplayed + 1, marginPagesDisplayed + 1); i <= totalPages; i++) {
            if (!pages.includes(i)) {
                pages.push(i);
            }
        }

        return pages;
    };

    /**
     * Handle page click
     */
    const handlePageClick = (page) => {
        if (page !== currentPage && page >= 1 && page <= totalPages) {
            onPageChange(page);
        }
    };

    /**
     * Handle previous button click
     */
    const handlePrevious = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    /**
     * Handle next button click
     */
    const handleNext = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    // Don't render if no pages
    if (totalPages <= 0) return null;

    const pageNumbers = getPageNumbers();

    return (
        <ul className="pagination">
            {/* Previous Button */}
            <li className={`pagination__item ${currentPage === 1 ? 'pagination__item--disabled' : ''}`}>
                <button
                    className="pagination__link pagination__nav"
                    onClick={handlePrevious}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                >
                    &lt;
                </button>
            </li>

            {/* Page Numbers */}
            {pageNumbers.map((page, index) => {
                if (typeof page === 'string' && page.startsWith('break')) {
                    return (
                        <li key={page} className="pagination__item pagination__item--break">
                            <span className="pagination__break">...</span>
                        </li>
                    );
                }

                return (
                    <li
                        key={page}
                        className={`pagination__item ${page === currentPage ? 'pagination__item--active' : ''}`}
                    >
                        <button
                            className="pagination__link"
                            onClick={() => handlePageClick(page)}
                            aria-label={`Page ${page}`}
                            aria-current={page === currentPage ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    </li>
                );
            })}

            {/* Next Button */}
            <li className={`pagination__item ${currentPage === totalPages ? 'pagination__item--disabled' : ''}`}>
                <button
                    className="pagination__link pagination__nav"
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                >
                    &gt;
                </button>
            </li>
        </ul>
    );
};

export default Pagination;
